import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NotFoundLink, NotFoundText, NotFoundWrapper } from 'app.style';
import { ConnectKitProvider } from 'connectkit';
import ChainIdProvider from 'contexts/ChainIdProvider';
import React, { Suspense, lazy } from 'react';
import ReactGA from 'react-ga';
import { Provider } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { WagmiProvider } from 'wagmi';
import BanksProvider from './contexts/Banks';
import ModalsProvider from './contexts/Modals';
import { RefreshContextProvider } from './contexts/RefreshContext';
import TombFinanceProvider from './contexts/TombFinanceProvider';
import Footer from './layout/footer';
import LandContainer from './layout/header';
import store from './state';
import Updaters from './state/Updaters';
import { config } from './wagmi.config';

const HomePage = lazy(() => import('./views/Home/HomePage'));
const NFTPage = lazy(() => import('./views/NFT'));
const SingleStakePage = lazy(() => import('./views/TigerStake'));
const NFTBid = lazy(() => import('views/NFT/NFTOfferPage'));

const NoMatch = () => (
  <NotFoundWrapper>
    <NotFoundText>
      Sorry, this URL was not found.
      <br />
      <NotFoundLink href="/">Go back home.</NotFoundLink>
    </NotFoundText>
  </NotFoundWrapper>
);

const loader = (
  <div
    style={{
      width: '100vw',
      height: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <div className="reverse-spinner" />
  </div>
);

const App: React.FC = () => {
  // Clear localStorage for mobile users
  if (typeof localStorage.version_app === 'undefined' || localStorage.version_app !== '1.1') {
    //localStorage.clear();
    localStorage.setItem('connectorId', '');
    localStorage.setItem('version_app', '1.1');
  }
  ReactGA.initialize('G-X7RLC4PELV');

  ReactGA.pageview(window.location.pathname);

  return (
    <Providers>
      <Suspense fallback={loader}>
        <LandContainer />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/nftcollection" element={<NFTPage />} />
          <Route path="/nftbid" element={<NFTBid gen={"1"} />} />
          <Route path="/singlestake" element={<SingleStakePage />} />
          <Route path="*" element={<NoMatch />} />
        </Routes>
          <Footer />
        </Suspense>
      <Suspense fallback={<div style={{color:'#fff',fontSize:'2rem',padding:'2rem'}}>Loading...</div>}>
        {/* This fallback will show if any lazy import fails or is loading */}
      </Suspense>
      {/* Root level portal container for modals */}
      <div id="modal-root" />
    </Providers>
  );
};

const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={new QueryClient()}>
        <ConnectKitProvider>
          <ChainIdProvider>
            <Provider store={store}>
              <Updaters />
              <RefreshContextProvider>
                <TombFinanceProvider>
                  <ModalsProvider>
                    <BanksProvider>
                      <>{children}</>
                    </BanksProvider>
                  </ModalsProvider>
                </TombFinanceProvider>
              </RefreshContextProvider>
            </Provider>
          </ChainIdProvider>
        </ConnectKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

export default App;
